/**
 * 018: COPIED from
 *   vendor/isomorphic/apps/isomorphic/src/app/auth/(otp)/otp-1/page.tsx (title/wrapper shape)
 *   wrapper: vendor/isomorphic/apps/isomorphic/src/app/shared/auth-layout/auth-wrapper-one.tsx
 *   with the field markup of vendor/isomorphic/apps/isomorphic/src/app/auth/(sign-in)/sign-in-1/
 *   sign-in-form.tsx
 *
 * The wrapper is the template's own file, imported from @steelyard/theme, banner column and hero
 * image included — not `auth-wrapper-three`'s bare card. The fields are rizzui
 * `Input` with the template's own size, label and input classes, wrapped in the template's own
 * `Form` (@core/ui/form.tsx) — the same composition as sign-in.tsx in this folder, not a
 * hand-wired `useForm`+`<form>`. The one-time-code field stays a plain `Input` rather than
 * rizzui's `PinCode` (used by every vendor OTP variant, e.g. `otp-1/otp-form.tsx`): Identity
 * issues exactly 6 digits, and one `Input` with `autoComplete="one-time-code"`, `maxLength={6}`
 * and a `[0-9]{6}` rule lets the phone's SMS autofill and a paste land in one field, where
 * `PinCode`'s six single-character boxes split them. `space-y-5` matches the copied file's
 * field-stack spacing throughout.
 *
 * Only the data, labels, routes and business rules are ours.
 */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { Button, Input, Text } from "rizzui";
import { AuthWrapperOne, UnderlineShape, Form, FieldError } from "@steelyard/theme";
import { landingAfterSignIn, requestOtp, safeReturnTo, verifyOtp } from "../../lib/identity";
import { useTurnstile } from "../../components/turnstile";
import heroImage from "../../assets/auth/sign-in-hero.svg";

/**
 * Ported from apps/admin/app/(auth)/otp/page.tsx + components/otp-form.tsx.
 *
 * Two steps, one route — which step renders is still decided by `?sent=1&contact=`, kept in the
 * URL (not local state) so a refresh on the verify step re-renders it instead of throwing the
 * person back to the start, same as the Next version. The code itself never touches the URL.
 *
 * The primary sign-in for warehouse staff (passwordless roles): one email field, then a 6-digit
 * code. Email only — see `otpContact` in lib/identity.ts for why a mobile is not offered. A
 * success is finished exactly like the password form's — the BFF has already written the session
 * cookies (after its `backoffice.access` check), and a full navigation to `returnTo` lets `rootLoader` run fresh against them.
 */
const OTP_CODE_SENT = "A 6-digit code is on its way to that email. Enter the code below.";
const OTP_REFUSED = "That code was not accepted. It may have expired or already been used — request another below.";
const OTP_THROTTLED = "Too many attempts have come from this connection. Wait a few minutes and try again.";

function otpRefusalText(status: number): string | null {
  if (status === 429) return OTP_THROTTLED;
  if (status === 401) return OTP_REFUSED;
  return null;
}

const contactSchema = z.object({
  contact: z.string().trim().min(1, "Enter your email address").email("Enter a valid email address"),
});
const codeSchema = z.object({ code: z.string().trim().regex(/^[0-9]{6}$/, "Enter the 6-digit code — numbers only") });
type ContactValues = z.infer<typeof contactSchema>;
type CodeValues = z.infer<typeof codeSchema>;

export default function OtpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contactParam = searchParams.get("contact") ?? "";
  const returnTo = safeReturnTo(searchParams.get("returnTo"));
  const verifying = searchParams.get("sent") === "1" && contactParam.length > 0;

  const [pending, setPending] = useState(false);
  const [refused, setRefused] = useState<number | null>(null);
  // Anything that is not a refusal — a malformed number (400), an account that may not use the
  // back office (the BFF's 403), an unreachable gateway — is rendered as the problem it is.
  const [submitError, setSubmitError] = useState<unknown>(null);
  // Shown on both steps: the first send spends a token, so a resend needs a fresh one.
  const captcha = useTurnstile("send_code");

  const handleRequest: SubmitHandler<ContactValues> = async (values) => {
    setSubmitError(null);
    try {
      await requestOtp(values.contact, await captcha.getToken());
      navigate(`/otp?${new URLSearchParams({ contact: values.contact, sent: "1", returnTo })}`);
    } catch (err) {
      setSubmitError(err);
    } finally {
      captcha.reset();
    }
  };

  const handleVerify: SubmitHandler<CodeValues> = async (values) => {
    setRefused(null);
    setSubmitError(null);
    try {
      const { refused: status } = await verifyOtp(contactParam, values.code);
      if (status) return setRefused(status);
      // A full navigation, not `navigate()` — the same reason sign-in.tsx gives.
      window.location.assign(await landingAfterSignIn(returnTo));
    } catch (err) {
      setSubmitError(err);
    }
  };

  async function handleResend() {
    setPending(true);
    setSubmitError(null);
    try {
      await requestOtp(contactParam, await captcha.getToken());
    } catch (err) {
      // The problem's own words: a 429 says how many seconds until another code may be sent.
      setSubmitError(err);
    } finally {
      setPending(false);
      captcha.reset();
    }
  }

  return (
    <AuthWrapperOne
      title={
        verifying ? (
          <>
            Enter your{" "}
            <span className="relative inline-block">
              code.
              <UnderlineShape className="absolute -bottom-2 end-0 h-2.5 w-16 text-blue xl:-bottom-1 xl:w-24" />
            </span>
          </>
        ) : (
          <>
            Sign in with a{" "}
            <span className="relative inline-block">
              one-time code.
              <UnderlineShape className="absolute -bottom-2 end-0 h-2.5 w-28 text-blue xl:-bottom-1.5 xl:w-36" />
            </span>
          </>
        )
      }
      bannerTitle="Infra Junction's operations, in one back office."
      bannerDescription="Orders, credit, GST compliance and warehouse dispatch — the same system your colleagues use, kept current with what the API actually returns."
      isSocialLoginActive={false}
      pageImage={
        <div className="relative mx-auto aspect-[4/3.37] h-[calc(100vh-31rem)] max-w-full">
          <img src={heroImage} alt="" className="absolute inset-0 size-full rounded-lg object-contain" />
        </div>
      }
    >
      {verifying ? null : (
        <Text className="-mt-4 mb-6 text-center leading-loose text-gray-500 lg:text-start">
          For warehouse staff — packers, supervisors and dispatchers. No password: we send a 6-digit code to your email.
        </Text>
      )}
      {submitError ? (
        <div className="mb-5">
          <FieldError error={submitError} />
        </div>
      ) : null}
      {verifying ? (
        <div className="space-y-5">
          <Text role="status" className="text-sm text-gray-500">
            {OTP_CODE_SENT}
          </Text>

          {refused ? (
            <FieldError message={otpRefusalText(refused) ?? undefined} error={otpRefusalText(refused) ? undefined : { status: refused }} />
          ) : null}

          <Form<CodeValues>
            validationSchema={codeSchema}
            onSubmit={handleVerify}
            useFormProps={{ defaultValues: { code: "" } }}
          >
            {({ register, formState: { errors, isSubmitting } }) => (
              <div className="space-y-5">
                <Input
                  size="lg"
                  className="[&>label>span]:font-medium"
                  inputClassName="text-sm"
                  label="One-time code"
                  autoFocus
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  helperText="The 6-digit code we sent you."
                  error={errors.code?.message}
                  {...register("code")}
                />

                <Button type="submit" className="w-full" size="lg" isLoading={isSubmitting}>
                  Sign in
                </Button>
              </div>
            )}
          </Form>

          {captcha.widget}
          <Button type="button" variant="text" size="lg" className="w-full" onClick={handleResend} isLoading={pending}>
            Send another code
          </Button>
        </div>
      ) : (
        <Form<ContactValues>
          validationSchema={contactSchema}
          onSubmit={handleRequest}
          useFormProps={{ defaultValues: { contact: contactParam } }}
        >
          {({ register, formState: { errors, isSubmitting } }) => (
            <div className="space-y-5">
              <Input
                size="lg"
                className="[&>label>span]:font-medium"
                inputClassName="text-sm"
                label="Email"
                type="email"
                autoFocus
                autoComplete="username"
                maxLength={254}
                helperText="The email address your invitation was sent to."
                error={errors.contact?.message}
                {...register("contact")}
              />
              {captcha.widget}
              <Button type="submit" className="w-full" size="lg" isLoading={isSubmitting}>
                Send me a code
              </Button>
            </div>
          )}
        </Form>
      )}

      <Text className="mt-6 text-center leading-loose text-gray-500 lg:mt-8 lg:text-start">
        Administrator?{" "}
        <Link to={returnTo === "/" ? "/sign-in" : `/sign-in?${new URLSearchParams({ returnTo })}`} className="font-semibold text-gray-700 transition-colors hover:text-blue">
          Sign in with your email and password
        </Link>
      </Text>
    </AuthWrapperOne>
  );
}
